/**
 * Choose clients for a roll-up, as AgencyAnalytics' "Choose clients": a
 * searchable list with Select All / Clear All and an "N Selected" count.
 * A roll-up covers at most 50 clients.
 */

import { useMemo, useState, type ReactNode } from 'react';

import type { Client } from './reports-api.ts';

export const MAX_ROLLUP_CLIENTS = 50;

export function ClientMultiPicker({
  clients,
  selected,
  onChange,
}: {
  clients: Client[];
  selected: string[];
  onChange: (ids: string[]) => void;
}): ReactNode {
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((client) => !term || client.name.toLowerCase().includes(term));
  }, [clients, search]);
  const chosen = new Set(selected);
  const toggle = (id: string) =>
    onChange(chosen.has(id) ? selected.filter((item) => item !== id) : [...selected, id]);
  const isFull = selected.length >= MAX_ROLLUP_CLIENTS;
  return (
    <div className="cm">
      <div className="cm-tools">
        <input
          type="search"
          placeholder="Search clients"
          aria-label="Search clients"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <button
          type="button"
          className="ex-link"
          onClick={() =>
            onChange(
              [...new Set([...selected, ...shown.map((client) => client.id)])].slice(
                0,
                MAX_ROLLUP_CLIENTS,
              ),
            )
          }
        >
          Select All
        </button>
        <button type="button" className="ex-link" onClick={() => onChange([])}>
          Clear All
        </button>
      </div>
      <p className="cm-count" role="status">
        {selected.length} Selected{isFull ? ` (the most a roll-up covers)` : ''}
      </p>
      <ul className="cm-list">
        {shown.map((client) => (
          <li key={client.id}>
            <label className="kp-check">
              <input
                type="checkbox"
                checked={chosen.has(client.id)}
                disabled={isFull && !chosen.has(client.id)}
                onChange={() => toggle(client.id)}
              />
              {client.name}
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="kp-faint">No client matches “{search}”.</li>}
      </ul>
    </div>
  );
}
